import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { newId, type Repositories } from '@core/db';
import type { CaseDocument, DocumentType } from '@core/domain/model';
import {
  buildPdfFromPhotos,
  canShareFiles,
  mergeDocuments,
  pageCountOf,
  shareFiles,
  type FileStore,
} from '@core/files';
import { readOneOf, writePreference } from '@core/settings/preferences';
import { deletionMessage, summary, typeOrder } from './formatter';
import { photoFileName, titleOf, titleOrUnsorted } from './imports';
import { entries, matches, order } from './ordering';
import type { DocumentsFilter, DocumentsSort, DocumentsState, PendingDeletion } from './types';
import { telemetry } from '@core/telemetry';

const SORT_KEY = 'documents.sort';
const SORTS = ['manual', 'byType', 'newest'] as const;

/**
 * Данные библиотеки документов дела.
 *
 * Роль вьюмодели: экран получает готовое состояние и зовёт действия, ничего не зная
 * ни про базу, ни про файловое хранилище — та же граница, что у `DocumentsRoute`
 * и `DocumentsScreen` на Android.
 */
export function useDocuments(caseId: string, db: Repositories, files: FileStore) {
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [caseTitle, setCaseTitle] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const [sort, setSortValue] = useState<DocumentsSort>(() => readOneOf(SORT_KEY, SORTS, 'byType'));
  const [filter, setFilter] = useState<DocumentsFilter>({ kind: 'all' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isReordering, setReordering] = useState(false);
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
  const [isImporting, setImporting] = useState(false);
  const [errorMessage, setError] = useState<string | null>(null);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const [item, ofCase] = await Promise.all([db.cases.find(caseId), db.documents.ofCase(caseId)]);
      if (dropped) return;

      setCaseTitle(item?.title ?? '');
      setDocuments(ofCase);
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, caseId, version]);

  /**
   * Файлы выбранных документов, прочитанные заранее.
   *
   * `navigator.share` открывает системный лист **только** пока держится жест: после
   * `await`, не связанного с нажатием, Safari молча отказывает. Поэтому содержимое
   * читается заранее — когда документ выделили или открыли его меню, — а само нажатие
   * «Поделиться» уже ничего не ждёт. Разбор — docs/PITFALLS.md §17.
   */
  const ready = useRef(new Map<string, File>());

  const prepare = useCallback(
    async (ids: string[]) => {
      for (const id of ids) {
        if (ready.current.has(id)) continue;

        const document = documents.find((item) => item.id === id);
        if (!document?.caseId) continue;

        const content = await files.open(document.caseId, document.fileName);
        if (!content.ok) continue;

        ready.current.set(
          id,
          new File([content.value], document.fileName, { type: document.mimeType }),
        );
      }
    },
    [documents, files],
  );

  const state: DocumentsState = useMemo(() => {
    const ordered = order(documents, sort, isReordering, dragOrder);
    const visible = ordered.filter((document) => matches(document, filter));

    return {
      isLoading,
      caseTitle,
      summary: summary(
        documents.length,
        documents.reduce((total, document) => total + document.sizeBytes, 0),
      ),
      entries: entries(visible, sort, isReordering),
      sort,
      filter,
      availableTypes: [...new Set(documents.map((document) => document.type))].sort(
        (a, b) => typeOrder(a) - typeOrder(b),
      ),
      hasFavorites: documents.some((document) => document.isFavorite),
      selectedIds,
      isReordering,
      pendingDeletion,
      isImporting,
      errorMessage,
    };
  }, [
    documents,
    caseTitle,
    sort,
    filter,
    isReordering,
    dragOrder,
    selectedIds,
    pendingDeletion,
    isImporting,
    errorMessage,
    isLoading,
  ]);

  /**
   * «Свой порядок» — не пресет, а просьба расставить документы руками, поэтому он
   * сразу включает перетаскивание. Выбор переживает перезапуск: юрист задал его раз.
   */
  const setSort = useCallback((next: DocumentsSort) => {
    setSortValue(next);
    writePreference(SORT_KEY, next);
    setReordering(next === 'manual');
    setDragOrder(null);
  }, []);

  /**
   * Кладёт одно содержимое в дело: сначала файл в хранилище, потом запись.
   *
   * Идентификатор выдаётся до записи — имя файла в хранилище это он. Порядок именно
   * такой: запись без файла означала бы документ, который нельзя открыть, а файл
   * без записи подберёт уборка осиротевших.
   */
  const addOne = useCallback(
    async (
      content: Blob,
      spec: {
        fileName: string;
        mimeType: string;
        title: string;
        type: DocumentType;
        source: CaseDocument['source'];
        pageCount: number | null;
      },
    ): Promise<boolean> => {
      const id = newId();
      const stored = await files.store(caseId, id, spec.fileName, content);
      if (!stored.ok) return false;

      // У принесённого PDF число страниц знает только сам файл — спрашиваем его.
      const pageCount =
        spec.pageCount ?? (spec.mimeType === 'application/pdf' ? await pageCountOf(content) : null);

      const result = await db.documents.add({
        id,
        title: spec.title,
        caseId,
        clientId: null,
        fileName: stored.value.fileName,
        mimeType: spec.mimeType,
        sizeBytes: stored.value.sizeBytes,
        pageCount,
        isFavorite: false,
        type: spec.type,
        source: spec.source,
      });
      return result.ok;
    },
    [db, files, caseId],
  );

  /**
 * Чем считать пришедшее с камеры.
 *
 * Снятое камерой в вебе всегда проходит через правку границ листа и выпрямление
 * (`ScanEditor`) — это и есть здешний сканер, замена ML Kit. Поэтому «снимков камерой»
 * в вебе не бывает: любой кадр — сканирование. Из галереи ничего не обрабатывается,
 * там может лежать уже готовый скан.
 */
const sourceEvent = (fromCamera: boolean) =>
  fromCamera ? ('documentScanned' as const) : ('documentFromGallery' as const);

/** Общая обвязка добавления: индикатор, счёт неудач, перечитывание списка. */
  const runImport = useCallback(
    async (total: number, work: () => Promise<number>) => {
      setImporting(true);
      const failed = await work().catch(() => total);
      setImporting(false);

      setError(
        failed === 0
          ? null
          : failed === 1
            ? 'Не удалось добавить документ'
            : `Не удалось добавить файлов: ${failed}`,
      );
      refresh();
    },
    [refresh],
  );

  const actions = useMemo(
    () => ({
      setSort,
      setFilter,
      finishReordering: () => setReordering(false),
      clearError: () => setError(null),
      prepare: (ids: string[]) => void prepare(ids),

      toggleSelection: (id: string) => {
        setSelectedIds((selected) =>
          selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id],
        );
        void prepare([id]);
      },

      clearSelection: () => setSelectedIds([]),

      async toggleFavorite(id: string, isFavorite: boolean) {
        // Считаем в обе стороны, как на Android: вопрос «пользуются ли избранным»,
        // а не «сколько звёзд стоит сейчас».
        telemetry.record('documentFavorited');
        await db.documents.setFavorite(id, isFavorite);
        refresh();
      },

      async rename(id: string, title: string) {
        const trimmed = title.trim();
        if (trimmed.length === 0) return;

        await db.documents.rename(id, trimmed);
        telemetry.record('documentRenamed');
        refresh();
      },

      /**
       * Тип документам: одному из меню строки или сразу пачке выделенных.
       *
       * Один метод на оба случая намеренно: пачка и одиночка отличаются только длиной
       * списка, а два метода разошлись бы поведением.
       */
      async setType(ids: string[], type: DocumentType) {
        // Действие над пачкой считается один раз: вопрос «как часто пользуются
        // возможностью», а не «сколько файлов затронуто».
        telemetry.record('documentTyped');
        for (const id of ids) await db.documents.setType(id, type);
        setSelectedIds([]);
        refresh();
      },

      /**
       * Удаление мягкое: запись скрывается, файл остаётся, пока действует «Отменить»
       * ([D-12](docs/DECISIONS.md)). Осиротевшие файлы убирает хранилище.
       */
      async remove(ids: string[]) {
        if (ids.length === 0) return;

        const titles = ids
          .map((id) => documents.find((document) => document.id === id)?.title)
          .filter((title): title is string => Boolean(title));

        for (const id of ids) await db.documents.remove(id);
        setSelectedIds([]);
        setPendingDeletion({ ids, message: deletionMessage(titles) });
        refresh();
      },

      async undoDelete() {
        if (!pendingDeletion) return;

        // Отмена объединения — это возврат частей и уборка того, что из них вышло.
        if (pendingDeletion.createdId) await db.documents.remove(pendingDeletion.createdId);
        for (const id of pendingDeletion.ids) await db.documents.restore(id);
        setPendingDeletion(null);
        refresh();
      },

      deletionShown: () => setPendingDeletion(null),

      /**
       * Порядок пишется **целиком** и один раз — по отпусканию ([D-37]).
       *
       * Порядок хранится полный, а на экране может быть видна лишь часть: включён
       * фильтр по типу или по избранному. Поэтому наверх приходит готовый список
       * идентификаторов, а не пара соседей.
       */
      async reorder(orderedIds: string[]) {
        // Невидимые сейчас документы остаются на своих местах в хвосте порядка.
        const hidden = documents
          .map((document) => document.id)
          .filter((id) => !orderedIds.includes(id));

        setDragOrder([...orderedIds, ...hidden]);
        telemetry.record('documentReordered');
        await db.documents.reorder(caseId, [...orderedIds, ...hidden]);
        setDragOrder(null);
        refresh();
      },

      /**
       * Системный лист «Поделиться» — одним документом или пачкой.
       *
       * Вызывается прямо из обработчика нажатия и берёт уже прочитанные файлы:
       * читать их здесь означало бы потерять жест и не открыть лист вовсе.
       */
      share(ids: string[], title: string): boolean {
        const prepared = ids
          .map((id) => ready.current.get(id))
          .filter((file): file is File => Boolean(file));

        if (prepared.length !== ids.length || !canShareFiles(prepared)) {
          void prepare(ids);
          setError('Не удалось открыть «Поделиться»');
          return false;
        }

        telemetry.record('documentsShared');
        void shareFiles(prepared, title).then((outcome) => {
          if (outcome === 'failed' || outcome === 'unsupported') {
            setError('Не удалось открыть «Поделиться»');
          }
          if (outcome === 'shared') setSelectedIds([]);
        });
        return true;
      },

      /**
       * Файлы устройства в дело: имя берётся из файла, тип задаётся диалогом.
       *
       * Приложение делает **собственную копию** файла ([D-02](docs/DECISIONS.md)):
       * юрист может удалить исходник из «Файлов», и материалы дела от этого не рвутся.
       */
      async addFiles(added: File[], type: DocumentType = 'Other', title: string | null = null) {
        if (added.length === 0) return;

        telemetry.record('documentFromFiles');

        await runImport(added.length, async () => {
          let failed = 0;
          for (const [index, file] of added.entries()) {
            const named = added.length === 1 && title ? title : titleOf(file.name);
            const ok = await addOne(file, {
              fileName: file.name,
              mimeType: file.type || 'application/octet-stream',
              title: named,
              type,
              source: 'Imported',
              pageCount: null,
            });
            if (!ok) failed += 1;
            void index;
          }
          return failed;
        });
      },

      /**
       * Снимки — по одному документу на кадр.
       *
       * Имени у снимка нет: назвали в диалоге — берём его, нажали «Потом» — документ
       * получает пометку «Не разобрано» с датой, чтобы в списке было видно, к чему
       * вернуться ([D-29](docs/DECISIONS.md)).
       */
      async addPhotos(photos: File[], type: DocumentType, title: string | null, fromCamera: boolean) {
        if (photos.length === 0) return;

        telemetry.record(sourceEvent(fromCamera));

        await runImport(photos.length, async () => {
          let failed = 0;
          for (const [index, photo] of photos.entries()) {
            const single = photos.length === 1;
            const ok = await addOne(photo, {
              fileName: photo.name || photoFileName(index),
              mimeType: photo.type || 'image/jpeg',
              title: titleOrUnsorted(single ? title : null, new Date(Date.now() + index)),
              type,
              source: fromCamera ? 'Photo' : 'Imported',
              pageCount: null,
            });
            if (!ok) failed += 1;
          }
          return failed;
        });
      },

      /**
       * Снимки одного документа — в один многостраничный PDF.
       *
       * Том дела это один документ, а не двадцать четыре строки в списке: юрист снимал
       * подряд одну вещь, ей и место одной строкой. Страницы разворачиваются по EXIF —
       * без этого они ложатся боком, и на Android это было первым, что заметил юрист.
       */
      async addPhotosAsOne(
        photos: File[],
        type: DocumentType,
        title: string | null,
        fromCamera: boolean,
      ) {
        if (photos.length === 0) return;

        telemetry.record(sourceEvent(fromCamera));

        await runImport(photos.length, async () => {
          const pdf = await buildPdfFromPhotos(photos);
          if (!pdf) return photos.length;

          const ok = await addOne(pdf.content, {
            fileName: 'document.pdf',
            mimeType: 'application/pdf',
            title: titleOrUnsorted(title, new Date()),
            type,
            source: fromCamera ? 'Photo' : 'Imported',
            pageCount: pdf.pageCount,
          });
          return ok ? 0 : photos.length;
        });
      },

      /**
       * Объединить выбранные документы в один.
       *
       * Отсканировала две страницы двумя заходами — получилось два документа, и свести
       * их было нельзя (обратная связь № 1, пункт 6). Исходники после склейки прячутся,
       * но не пропадают: «Отменить» вернёт их и уберёт получившийся документ — то же
       * правило, что при удалении ([D-12](docs/DECISIONS.md)).
       */
      async merge(ids: string[], title: string) {
        const parts = ids
          .map((id) => documents.find((document) => document.id === id))
          .filter((document): document is CaseDocument => Boolean(document));
        if (parts.length < 2) return;

        setImporting(true);

        const contents = [];
        for (const part of parts) {
          if (!part.caseId) continue;
          const stored = await files.open(part.caseId, part.fileName);
          if (stored.ok) contents.push({ content: stored.value, mimeType: part.mimeType });
        }

        const merged = await mergeDocuments(contents).catch(() => null);
        if (!merged) {
          setImporting(false);
          setError('Не удалось объединить');
          return;
        }

        const createdId = newId();
        const stored = await files.store(caseId, createdId, 'merged.pdf', merged.content);
        const added =
          stored.ok &&
          (
            await db.documents.add({
              id: createdId,
              title: title.trim() || 'Объединённый документ',
              caseId,
              clientId: null,
              fileName: stored.value.fileName,
              mimeType: 'application/pdf',
              sizeBytes: stored.value.sizeBytes,
              pageCount: merged.pageCount,
              isFavorite: false,
              type: parts[0]?.type ?? 'Other',
              source: 'Scanned',
            })
          ).ok;

        if (!added) {
          setImporting(false);
          setError('Не удалось объединить');
          return;
        }

        for (const part of parts) await db.documents.remove(part.id);

        setImporting(false);
        setSelectedIds([]);
        setPendingDeletion({
          ids: parts.map((part) => part.id),
          message: `Объединено документов: ${parts.length}`,
          createdId,
        });
        refresh();
      },

      refresh,
    }),
    [db, files, caseId, addOne, runImport, documents, pendingDeletion, prepare, refresh, setSort],
  );

  return { state, actions };
}

export type DocumentsActions = ReturnType<typeof useDocuments>['actions'];
